import { ApiError, handleApiError } from "@/api/apiError";
import {
  createLeaveRequest,
  deleteRequest,
  getLeaveRequests,
  getLeaveRequestsCountByStatus,
  getLeaveRequestsOfUser,
  getUserLeaveRequestsCountByStatus,
  updateLeaveRequestStatus,
} from "@/api/leave-requests";
import {
  CreateLeaveRequestArgs,
  LeaveRequest,
  LeaveRequestDTO,
} from "@/models/leave-requests.model";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

interface LeaveRequestState {
  adminRequests: LeaveRequest[];
  adminRequestsFilters: {
    search: string;
    status: string[];
    type: string[];
  };
  userRequestsFilters: {
    status: string[];
    type: string[];
  };
  userRequests: LeaveRequest[];
  adminRequestsLoading: boolean;
  userRequestsLoading: boolean;
  error: ApiError | null;
  adminPagination: {
    currentPage: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
  };
  userPagination: {
    currentPage: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
  };
  adminCounts: {
    PENDING: number;
    APPROVED: number;
    REJECTED: number;
    TOTAL: number;
  };
  userCounts: {
    PENDING: number;
    APPROVED: number;
    REJECTED: number;
    TOTAL: number;
  };
  adminCountsLoading: boolean;
  userCountsLoading: boolean;
}
const initialState: LeaveRequestState = {
  adminRequests: [],
  adminRequestsFilters: {
    search: "",
    status: [],
    type: [],
  },
  userRequests: [],
  userRequestsFilters: {
    status: [],
    type: [],
  },
  adminRequestsLoading: false,
  userRequestsLoading: false,
  error: null,
  adminPagination: {
    currentPage: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,
  },
  userPagination: {
    currentPage: 0,
    pageSize: 10,
    totalElements: 0,
    totalPages: 0,
  },
  adminCounts: {
    PENDING: 0,
    APPROVED: 0,
    REJECTED: 0,
    TOTAL: 0,
  },
  userCounts: {
    PENDING: 0,
    APPROVED: 0,
    REJECTED: 0,
    TOTAL: 0,
  },
  adminCountsLoading: false,
  userCountsLoading: false,
};

export interface PaginatedLeaveResponse  {
  currentPage: number;
  pageSize: number;
  total: number;
  totalPages: number;
  data:LeaveRequestDTO[];
}


// Async thunks

export const fetchLeaveRequests = createAsyncThunk(
  'leaveRequests/fetch',
  async ({page = 1, limit=7, search="", status, type}: {page?: number; limit?: number; search?: string, status?: string[], type?: string[]}, {rejectWithValue}) => {
    try {
      return await getLeaveRequests({page, limit, search, status, type});
    } catch (err: any) {
    return rejectWithValue(handleApiError(err));
    }  
  }
);

export const fetchLeaveRequestsOfUser = createAsyncThunk(
  'leaveRequests/fetchUserLeaveRequests',
  async ({userId, page = 1, limit=7, status, type}: {userId: number, page?: number; limit?: number; status?: string[]; type?: string[]}, { rejectWithValue }) => {
    try {
      const res = await getLeaveRequestsOfUser({
        userId,
        page,
        limit,
        status,
        type,
      });
      return res;
    } catch (err: any) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const removeLeaveRequest = createAsyncThunk(
  'leaveRequests/delete',
  async (leaveId: number, {rejectWithValue}) => {
    try {
      await deleteRequest(leaveId);
      return leaveId;
    } catch (err: any) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const approveOrRejectLeaveRequest = createAsyncThunk(
  'leaveRequests/updateStatus',
  async ({ isApproved, leaveRequestId }: { isApproved: boolean; leaveRequestId: number }, {rejectWithValue}) => {
    try {
      const res: LeaveRequest = await updateLeaveRequestStatus(
        isApproved,
        leaveRequestId
      );
      return res;
    } catch (err: any) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const addLeaveRequest = createAsyncThunk(
  'leaveRequests/create',
  async (args: CreateLeaveRequestArgs, { rejectWithValue }) => {
    try {
      return await createLeaveRequest(args);
    } catch (error: any) {
      return rejectWithValue(handleApiError(error));
    }
  }
);


export const fetchAllLeaveRequestsCount = createAsyncThunk(
  'leaveRequests/fetchLeaveRequestCounts',
  async (_, { rejectWithValue }) => {
    try {
      
      return await getLeaveRequestsCountByStatus()
     
    } catch (err: any) {
      return rejectWithValue(handleApiError(err));
    }})

export const fetchUserLeaveRequestsCount = createAsyncThunk(
  'leaveRequests/fetchUserLeaveRequestCounts',
  async (userId: number, { rejectWithValue }) => {
    try {
      return await getUserLeaveRequestsCountByStatus(userId);
    } catch (err: any) {
      return rejectWithValue(handleApiError(err));
    }
  }
);



const leaveRequestSlice = createSlice({
  name: 'leaveRequests',
  initialState,
  reducers: {
    // Any additional synchronous reducers
    clearErrors: (state) => {
      state.error = null;
    },
    clearLeaveRequests: (state) => {
      state.adminRequests = initialState.adminRequests;
      state.userRequests = initialState.userRequests;
      state.adminRequestsLoading = initialState.adminRequestsLoading;
      state.userRequestsLoading = initialState.userRequestsLoading;
      state.error = initialState.error;
      state.adminPagination = initialState.adminPagination;
      state.adminCounts = initialState.adminCounts;
      state.userCounts = initialState.userCounts;
      state.adminCountsLoading = initialState.adminCountsLoading;
      state.userCountsLoading = initialState.userCountsLoading;
    },
    setAdminSearch: (state, action: PayloadAction<string>) => {
      state.adminRequestsFilters.search = action.payload;
    },
    setAdminStatus: (state, action: PayloadAction<string[]>) => {
      state.adminRequestsFilters.status = action.payload;
    },
    setUserStatus: (state, action: PayloadAction<string[]>) => {
      state.userRequestsFilters.status = action.payload;
    },
    setAdminType: (state, action: PayloadAction<string[]>) => {
      state.adminRequestsFilters.type = action.payload;
    }
    ,
    setUserType: (state, action: PayloadAction<string[]>) => {
      state.userRequestsFilters.type = action.payload;
    },
    resetAdminFilters: (state) => {
      state.adminRequestsFilters.search = "";
      state.adminRequestsFilters.status = [];
      state.adminRequestsFilters.type = [];
    }
    ,
    resetUserFilters: (state) => {
      console.log("resetting user filters");
      state.userRequestsFilters.status = [];
      state.userRequestsFilters.type = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all leave requests
      .addCase(fetchLeaveRequests.pending, (state) => {
        state.adminRequestsLoading = true;
        state.error = null;
      })
      .addCase(fetchLeaveRequests.fulfilled, (state, action: PayloadAction<PaginatedLeaveResponse>) => {
        state.adminRequestsLoading = false;
        state.adminRequests = action.payload.data;
        state.adminPagination = {
          currentPage: action.payload.currentPage,
          pageSize: action.payload.pageSize,
          totalElements: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchLeaveRequests.rejected, (state, action) => {
        state.adminRequestsLoading = false;
        state.error = action.payload as ApiError || {
          message: 'Failed to fetch leave requests',
          error: action.error.message || 'An unknown error occurred',
          statusCode: 500
        };
      })
      // Fetch all leave requests
      .addCase(fetchLeaveRequestsOfUser.pending, (state) => {
        state.userRequestsLoading = true;
        state.error = null;
      })
      .addCase(fetchLeaveRequestsOfUser.fulfilled, (state, action: PayloadAction<PaginatedLeaveResponse>) => {
        state.userRequestsLoading = false;
        state.userRequests = action.payload.data;
        state.userPagination = {
          currentPage: action.payload.currentPage,
          pageSize: action.payload.pageSize,
          totalElements: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchLeaveRequestsOfUser.rejected, (state, action) => {
        state.userRequestsLoading = false;
        state.error = action.payload as ApiError || {
          message: 'Failed to fetch leave requests',
          error: action.error.message || 'An unknown error occurred',
          statusCode: 500
        };
      })
      // Fetch counts of all leave requests 
    .addCase(fetchAllLeaveRequestsCount.pending, (state) => {
      state.adminCountsLoading = true;
    })
    .addCase(fetchAllLeaveRequestsCount.fulfilled, (state, action) => {
      state.adminCountsLoading = false;
      state.adminCounts = action.payload;
    })
    .addCase(fetchAllLeaveRequestsCount.rejected, (state, action) => {
      state.adminCountsLoading = false;
      state.error = action.payload as ApiError || {
        message: 'Failed to fetch leave requests count',
        error: action.error.message || 'An unknown error occurred',
        statusCode: 500
      };
    })
    // Fetch counts of user leave requests 
    .addCase(fetchUserLeaveRequestsCount.pending, (state) => {
      state.userCountsLoading = true;
    })
    .addCase(fetchUserLeaveRequestsCount.fulfilled, (state, action) => {
      state.userCountsLoading = false;
      state.userCounts = action.payload;
    })
    .addCase(fetchUserLeaveRequestsCount.rejected, (state, action) => {
      state.userCountsLoading = false;
      state.error = action.payload as ApiError || {
        message: 'Failed to fetch leave requests count',
        error: action.error.message || 'An unknown error occurred',
        statusCode: 500
      };
    })
      // Delete request -> user operation
      .addCase(removeLeaveRequest.pending, (state) => {
        state.userRequestsLoading = true;
        state.error = null;
      })
      .addCase(removeLeaveRequest.fulfilled, (state, action: PayloadAction<number>) => {
        state.userRequestsLoading = false;
        state.userRequests = state.userRequests.filter(request => request.id !== action.payload);
      })
      .addCase(removeLeaveRequest.rejected, (state, action) => {
        state.userRequestsLoading = false;
        state.error = action.payload as ApiError || {
          message: 'Failed to delete leave request',
          error: action.error.message || 'An unknown error occurred',
          statusCode: 500
        };
      })
      
      // Update status -> admin operation
      .addCase(approveOrRejectLeaveRequest.pending, (state) => {
        state.adminRequestsLoading = true;
        state.error = null;
      })
      .addCase(approveOrRejectLeaveRequest.fulfilled, (state, action: PayloadAction<LeaveRequest>) => {
        state.adminRequestsLoading = false;
        const index = state.adminRequests.findIndex(request => request.id === action.payload.id);
        if (index !== -1) {
          state.adminRequests[index] = action.payload;
        }
      })
      .addCase(approveOrRejectLeaveRequest.rejected, (state, action) => {
        state.adminRequestsLoading = false;
        state.error = action.payload as ApiError || {
          message: 'Failed to update leave request',
          error: action.error.message || 'An unknown error occurred',
          statusCode: 500
        };
      })
      
      // Create request -> user operation
      .addCase(addLeaveRequest.pending, (state) => {
        state.adminRequestsLoading = true;
        state.error = null;
      })
      .addCase(addLeaveRequest.fulfilled, (state, action: PayloadAction<LeaveRequest>) => {
        state.adminRequestsLoading = false;
        state.adminRequests.push(action.payload);
      })
      .addCase(addLeaveRequest.rejected, (state, action) => {
        state.adminRequestsLoading = false;
        state.error = action.payload as ApiError || {
          message: 'Failed to create leave request',
          error: action.error.message || 'An unknown error occurred',
          statusCode: 500
        };
      })
  },
});

export const { 
  clearErrors,
  clearLeaveRequests,
  setAdminSearch,
  setAdminStatus,
  setUserStatus,
  setAdminType,
  setUserType,
  resetAdminFilters,
  resetUserFilters
 } = leaveRequestSlice.actions;
export default leaveRequestSlice.reducer;
